/*
 * cart-checkout-gate.js — defence-in-depth for the free-gift promo.
 *
 * PROBLEM
 *   assets/cart.js addresses cart lines by POSITION when it calls
 *   /cart/change.js. When assets/gift-progress.js concurrently adds or removes
 *   the free-gift line, positions shift and a shopper's in-flight quantity
 *   change can land on the gift line, inflating it (e.g. gift qty 10).
 *   gift-progress.js already self-heals this (any gift line is forced back to
 *   qty 1 on its next sync), but there is a sub-second window between the
 *   shopper's change settling and that heal running.
 *
 * WHAT THIS DOES
 *   Disables the Checkout buttons (#CartDrawer-Checkout, #checkout) while ANY
 *   cart write — the shopper's or gift-progress's — is in flight, and while a
 *   gift sync is scheduled or running. Re-enables when everything is settled.
 *
 * WHAT THIS IS NOT
 *   Not the primary control. The primary control is a native Shopify
 *   "Buy X Get Y" automatic discount capped at quantity 1, which makes the
 *   financial exposure zero regardless of the client. This gate only tidies UX
 *   so a shopper cannot click Checkout mid-race.
 *
 * NOT COVERED (accepted — the native discount is the backstop)
 *   - Accelerated checkout buttons on the cart page (Shop Pay / PayPal express,
 *     sections/main-cart-footer.liquid ~line 100): iframe-based, un-gateable.
 *   - form.submit() called directly from script (the dead IE fallback blocks in
 *     cart-drawer.liquid / main-cart-footer.liquid): fires no 'submit' event.
 *   - The ~250ms settle window that bridges cart.js's silent-retry /cart.js
 *     round-trip (a genuine cart-error path, rare).
 *
 * COUPLING
 *   - Monkey-patches window.withCartLock (assets/global.js). global.js is
 *     hardened to `window.withCartLock = window.withCartLock || function…` so a
 *     duplicated global.js cannot un-patch this. If a future build step bundles
 *     global.js + cart.js + gift-progress.js and makes withCartLock a
 *     module-local binding, this gate silently no-ops (fails safe: Checkout
 *     stays clickable) and gift-progress's clamp + the native discount hold.
 *   - Listens for PUB_SUB_EVENTS.giftSyncPending / giftSyncDone, published by
 *     gift-progress.js. No timing constant is shared between the two files.
 *
 * Load order: after constants.js + pubsub.js + global.js (all head-defer) and
 * immediately BEFORE gift-progress.js (so window.withCartLock is wrapped before
 * gift-progress init() calls it).
 */
(function () {
  if (window.__checkoutGateInstalled) return;
  window.__checkoutGateInstalled = true;

  // Bridges cart.js's silent-retry /cart.js round-trip and near-simultaneous
  // event flicker. NOT tied to gift-progress timing — that is covered by the
  // giftSyncPending / giftSyncDone signals.
  var SETTLE_DEBOUNCE_MS = 250;
  // Absolute deadline. If a cart write never settles (fetch has no timeout),
  // force Checkout back on rather than trap the shopper.
  var MAX_DISABLED_MS = 10000;

  var CHECKOUT_SELECTOR = '#CartDrawer-Checkout, #checkout';
  var CONTAINER_SELECTORS = ['#CartDrawer', '#main-cart-footer', '#main-cart-items'];

  var hasSet = typeof Set === 'function';
  var pendingSet = hasSet ? new Set() : null;
  var pendingArr = hasSet ? null : [];
  var giftSyncPending = false;
  var desiredDisabled = false;
  var openTimer = null;
  var watchdogTimer = null;
  var rafScheduled = false;

  function logError(where, err) {
    try {
      if (window.console && console.error) console.error('[checkout-gate] ' + where, err);
    } catch (e) {}
  }

  // ---- pending set (with fallback for very old engines) ------------------
  function pendingAdd(token) {
    if (pendingSet) pendingSet.add(token);
    else pendingArr.push(token);
  }
  function pendingDelete(token) {
    if (pendingSet) {
      pendingSet['delete'](token);
      return;
    }
    var i = pendingArr.indexOf(token);
    if (i !== -1) pendingArr.splice(i, 1);
  }
  function pendingSize() {
    return pendingSet ? pendingSet.size : pendingArr.length;
  }
  function pendingClear() {
    if (pendingSet) pendingSet.clear();
    else pendingArr.length = 0;
  }

  // ---- button state (idempotent: writes only on a real mismatch, so the
  //      MutationObserver below cannot feed back into an infinite loop) -----
  function render() {
    try {
      var btns = document.querySelectorAll(CHECKOUT_SELECTOR);
      for (var i = 0; i < btns.length; i++) {
        var btn = btns[i];
        var isGated = btn.hasAttribute('data-gate-disabled');
        if (desiredDisabled && !isGated) {
          btn.setAttribute('data-gate-disabled', '');
          btn.setAttribute('disabled', '');
          btn.setAttribute('aria-busy', 'true');
        } else if (!desiredDisabled && isGated) {
          // Only ever clears the disabled state WE set (tracked by the marker
          // attribute) — never the `disabled` Liquid emits for an empty cart.
          btn.removeAttribute('data-gate-disabled');
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-busy');
        }
      }
    } catch (e) {
      logError('render', e);
    }
  }

  function setDisabled(next) {
    desiredDisabled = next;
    render();
  }

  function scheduleRender() {
    if (rafScheduled) return;
    rafScheduled = true;
    var run = function () {
      rafScheduled = false;
      render();
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 16);
  }

  // ---- open / close ----------------------------------------------------
  function shouldBeOpen() {
    return pendingSize() === 0 && !giftSyncPending;
  }

  function maybeOpen() {
    if (openTimer !== null) {
      clearTimeout(openTimer);
      openTimer = null;
    }
    if (!shouldBeOpen()) return;
    openTimer = setTimeout(function () {
      openTimer = null;
      if (shouldBeOpen()) setDisabled(false);
    }, SETTLE_DEBOUNCE_MS);
  }

  function holdClosed() {
    if (openTimer !== null) {
      clearTimeout(openTimer);
      openTimer = null;
    }
    setDisabled(true);
  }

  // ---- watchdog: absolute deadline, armed on the idle->busy edge only,
  //      never re-armed while busy, cleared only when fully idle ------------
  function armWatchdog() {
    if (watchdogTimer !== null) return;
    watchdogTimer = setTimeout(function () {
      watchdogTimer = null;
      pendingClear();
      giftSyncPending = false;
      if (openTimer !== null) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      setDisabled(false);
      logError('watchdog', 'force-enabled Checkout after a stuck cart request');
    }, MAX_DISABLED_MS);
  }
  function clearWatchdog() {
    if (watchdogTimer !== null) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
  }

  function onIdleReached() {
    // Called whenever pendingSize() may have hit 0. Only fully idle if no gift
    // sync is outstanding either.
    if (pendingSize() === 0 && !giftSyncPending) clearWatchdog();
    maybeOpen();
  }

  // ---- wrap window.withCartLock --------------------------------------
  var original = window.withCartLock;
  if (typeof original === 'function' && !original.__gateWrapped) {
    var wrapped = function (task) {
      // Do the real cart write FIRST and unconditionally. Nothing the gate does
      // below can prevent it or change what the caller receives.
      var settled = original(task);
      try {
        var token = {};
        pendingAdd(token);
        holdClosed();
        armWatchdog();

        var release = function () {
          try {
            pendingDelete(token);
            if (pendingSize() === 0) onIdleReached();
          } catch (e) {
            logError('release', e);
          }
        };

        if (settled && typeof settled.then === 'function') settled.then(release, release);
        else release();
      } catch (e) {
        logError('acquire', e);
      }
      return settled;
    };
    wrapped.__gateWrapped = true;
    try {
      window.withCartLock = wrapped;
    } catch (e) {
      logError('patch', e);
    }
  }

  // ---- gift-sync signals from gift-progress.js ---------------------
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    if (PUB_SUB_EVENTS.giftSyncPending) {
      subscribe(PUB_SUB_EVENTS.giftSyncPending, function () {
        giftSyncPending = true;
        holdClosed();
        armWatchdog();
      });
    }
    if (PUB_SUB_EVENTS.giftSyncDone) {
      subscribe(PUB_SUB_EVENTS.giftSyncDone, function () {
        giftSyncPending = false;
        onIdleReached();
      });
    }
    if (PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        // cart.js has just swapped section HTML — re-assert on the fresh button.
        if (desiredDisabled) scheduleRender();
      });
    }
  }

  // ---- re-assert across drawer / cart re-renders (gift-progress.js swaps
  //      .drawer__inner without publishing cartUpdate) ---------------------
  if (typeof MutationObserver === 'function') {
    try {
      var observer = new MutationObserver(function () {
        if (desiredDisabled) scheduleRender();
      });
      for (var i = 0; i < CONTAINER_SELECTORS.length; i++) {
        var el = document.querySelector(CONTAINER_SELECTORS[i]);
        if (el) observer.observe(el, { childList: true, subtree: true });
      }
    } catch (e) {
      logError('observer', e);
    }
  }

  // ---- backstop: stop form submission while gated (covers Enter-key
  //      implicit submit and any future extra submit button) --------------
  document.addEventListener(
    'submit',
    function (e) {
      if (!desiredDisabled) return;
      try {
        var form = e.target;
        var formGated =
          form && form.tagName === 'FORM' && (form.id === 'CartDrawer-Form' || form.id === 'cart');
        var s = e.submitter;
        var submitterGated =
          s && (s.name === 'checkout' || s.id === 'checkout' || s.id === 'CartDrawer-Checkout');
        if (formGated || submitterGated) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      } catch (err) {
        logError('submit', err);
      }
    },
    true
  );

  // ---- reset stale state on bfcache restore --------------------------
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    pendingClear();
    giftSyncPending = false;
    if (openTimer !== null) {
      clearTimeout(openTimer);
      openTimer = null;
    }
    clearWatchdog();
    setDisabled(false);
  });
})();
